import { coreConfig, assertCoreConfig } from '../config/core';
import { authStorage } from './authStorage';
import {
  ApplicationOnboardingRequest,
  ApplicationOnboardingResponse,
  AuthTokenResponse,
  CoreTenant,
  CoreUser,
  ExchangeOnboardingHandoffRequest,
  ExchangeOnboardingHandoffResponse,
  RefreshTokenResponse,
} from '../types';

export class CoreApiError extends Error {
  constructor(
    message: string,
    public status: number
  ) {
    super(message);
    this.name = 'CoreApiError';
  }
}

interface ApiErrorBody {
  message?: string;
}

async function parseErrorResponse(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as ApiErrorBody;
    return body.message || response.statusText || 'Request failed';
  } catch {
    return response.statusText || 'Request failed';
  }
}

async function coreFetch<T>(
  path: string,
  options: RequestInit & { auth?: boolean; accessToken?: string } = {}
): Promise<T> {
  assertCoreConfig();
  const { auth = false, headers, accessToken, ...rest } = options;

  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(headers as Record<string, string>),
  };

  if (auth) {
    const token = accessToken ?? authStorage.getToken();
    if (token) {
      requestHeaders.Authorization = `Bearer ${token}`;
    }
  }

  let response: Response;
  try {
    response = await fetch(`${coreConfig.apiBaseUrl}${path}`, {
      ...rest,
      headers: requestHeaders,
    });
  } catch {
    throw new CoreApiError('Unable to reach the sign-in service. Check your connection and try again.', 0);
  }

  if (!response.ok) {
    const message = await parseErrorResponse(response);
    throw new CoreApiError(message, response.status);
  }

  return response.json() as Promise<T>;
}

export async function requestAuthToken(body: {
  applicationId: string;
  email: string;
  password: string;
}): Promise<AuthTokenResponse> {
  return coreFetch<AuthTokenResponse>('/auth/token', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function submitApplicationOnboarding(
  body: ApplicationOnboardingRequest
): Promise<ApplicationOnboardingResponse> {
  return coreFetch<ApplicationOnboardingResponse>('/application-onboarding', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function exchangeOnboardingHandoff(
  body: ExchangeOnboardingHandoffRequest
): Promise<ExchangeOnboardingHandoffResponse> {
  return coreFetch<ExchangeOnboardingHandoffResponse>('/onboarding/handoff/exchange', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function getProfile(): Promise<CoreUser> {
  const response = await coreFetch<CoreUser & { user?: CoreUser }>('/auth/profile', {
    method: 'GET',
    auth: true,
  });
  return response.user ?? response;
}

export async function refreshAuthToken(refreshToken: string): Promise<RefreshTokenResponse> {
  assertCoreConfig();
  const response = await fetch(`${coreConfig.apiBaseUrl}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });

  if (!response.ok) {
    const message = await parseErrorResponse(response);
    throw new CoreApiError(message, response.status);
  }

  return response.json() as Promise<RefreshTokenResponse>;
}

export async function getApplicationTenants(): Promise<CoreTenant[]> {
  const raw = await coreFetch<CoreTenant[] | { data?: CoreTenant[]; tenants?: CoreTenant[] }>(
    '/auth/application/tenants',
    { method: 'GET', auth: true },
  );
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw.data)) return raw.data;
  if (Array.isArray(raw.tenants)) return raw.tenants;
  return [];
}

export async function selectTenant(
  tenantId: string,
  accessToken?: string,
): Promise<AuthTokenResponse> {
  return coreFetch<AuthTokenResponse>('/auth/select-tenant', {
    method: 'POST',
    auth: true,
    accessToken,
    body: JSON.stringify({ tenantId }),
  });
}

export async function selectSite(
  tenantSiteId: string,
  accessToken?: string,
): Promise<AuthTokenResponse> {
  return coreFetch<AuthTokenResponse>('/auth/select-site', {
    method: 'POST',
    auth: true,
    accessToken,
    body: JSON.stringify({ tenantSiteId }),
  });
}

export async function logoutFromCore(refreshToken: string): Promise<void> {
  try {
    assertCoreConfig();
    await fetch(`${coreConfig.apiBaseUrl}/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
  } catch {
    // Logout errors are non-fatal; client-side session is already cleared
  }
}

